import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, formatWIB, toNum, wibNowLocal, wibLocalToUTCISO } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { MoneyInput } from "@/components/MoneyInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Plus, Wallet, Trash2, Settings2, Layers, CalendarClock, CheckCircle2, Clock } from "lucide-react";

// ==== Helper tanggal WIB untuk folder bulan -> tanggal (selaras dgn halaman Transaksi) ====
const BULAN_ID_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const HARI_ID_FULL = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const monthKeyWIB = (iso) => {
  try { const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000); return `${d.getUTCFullYear()}-${d.getUTCMonth()}`; } catch { return ""; }
};
const monthLabelWIB = (iso) => {
  try { const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000); return `${BULAN_ID_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}`; } catch { return "-"; }
};
const dayKeyWIB = (iso) => {
  try { const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000); return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`; } catch { return ""; }
};
const dayLabelWIB = (iso) => {
  try { const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000); return `${HARI_ID_FULL[d.getUTCDay()]}, ${d.getUTCDate()} ${BULAN_ID_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}`; } catch { return "-"; }
};
const nowMonthInput = () => {
  const d = new Date(new Date().getTime() + 7 * 3600 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthInputToKey = (mi) => {
  const [y, m] = (mi || "").split("-").map(Number);
  if (!y || !m) return "";
  return `${y}-${m - 1}`;
};
const monthInputLabel = (mi) => {
  const [y, m] = (mi || "").split("-").map(Number);
  if (!y || !m) return "-";
  return `${BULAN_ID_FULL[m - 1]} ${y}`;
};

// ==== Jadwal cicilan: hari kerja berurutan; Sabtu+Minggu = 1 hari (slot di Sabtu) ====
const _snapWorkDay = (d) => { const nd = new Date(d); if (nd.getDay() === 0) nd.setDate(nd.getDate() - 1); return nd; };
const _nextWorkDay = (d) => { const nd = new Date(d); nd.setDate(nd.getDate() + 1); if (nd.getDay() === 0) nd.setDate(nd.getDate() + 1); return nd; };
const computeSchedule = (total, days, startDate) => {
  const t = Number(total) || 0; const n = Math.max(1, parseInt(days) || 1);
  const base = Math.round(t / n);
  const amounts = Array(n - 1).fill(base); amounts.push(Math.round(t - base * (n - 1)));
  let d = _snapWorkDay(startDate ? new Date(startDate) : new Date());
  const slots = [];
  for (let i = 0; i < n; i++) { slots.push({ date: new Date(d), amount: amounts[i] }); d = _nextWorkDay(d); }
  return slots;
};
const dateShort = (d) => {
  try { const x = typeof d === "string" ? new Date(d) : d; return `${x.getDate()} ${BULAN_ID_FULL[x.getMonth()].slice(0, 3)} ${x.getFullYear()}`; } catch { return "-"; }
};

export default function Expenses() {
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [apps, setApps] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [manage, setManage] = useState(false);
  const [manageInst, setManageInst] = useState(false);
  const [monthSel, setMonthSel] = useState(nowMonthInput());
  const [allMode, setAllMode] = useState(false);
  const [installments, setInstallments] = useState([]);

  const loadCats = () => api.get("/expenses/categories").then((r) => setCats(r.data));
  const loadInst = () => api.get("/expense-installments").then((r) => setInstallments(r.data)).catch(() => setInstallments([]));
  const load = () => {
    Promise.all([api.get("/expenses"), api.get("/apps")]).then(([e, a]) => { setList(e.data); setApps(a.data); });
    loadCats();
    loadInst();
  };
  useEffect(() => { load(); }, []);

  const del = async (row) => {
    const isBeban = row?.beban_only || row?.source === "installment";
    const ok = await confirmDialog(isBeban
      ? { title: "Hapus beban cicilan ini?", description: "Baris ini adalah bagian dari sebuah cicilan. Menghapusnya akan menghapus SELURUH cicilan terkait beserta semua bebannya." }
      : { title: "Hapus pengeluaran?", description: "Data pengeluaran ini akan dihapus permanen." });
    if (!ok) return;
    const r = await api.delete(`/expenses/${row.id}`);
    toast.success(r?.data?.installment_deleted ? "Cicilan & bebannya dihapus" : "Terhapus"); load();
  };

  // Urutkan terbaru dulu agar folder bulan -> tanggal rapi
  const sorted = useMemo(() => [...list].sort((a, b) => (b.date || "").localeCompare(a.date || "")), [list]);
  const visibleList = useMemo(() => {
    if (allMode) return sorted;
    const key = monthInputToKey(monthSel);
    return sorted.filter((r) => monthKeyWIB(r.date) === key);
  }, [sorted, allMode, monthSel]);
  const total = visibleList.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  const srcName = (e) => {
    if (e.beban_only || e.source === "installment") return "Cicilan";
    return e.source === "app" ? (apps.find((a) => a.id === e.source_id)?.name || "-") : "Kas Fisik";
  };

  const activeInst = installments.filter((i) => i.status === "active");
  const totalSaldoCicilan = installments.reduce((s, i) => s + Number(i.outstanding || 0), 0);

  const columns = [
    { key: "date", label: "Tanggal & Jam", sortable: true, sortValue: (r) => r.date || "", render: (r) => <span className="text-sm">{formatWIB(r.date)}</span> },
    { key: "category", label: "Kategori", sortable: true, filter: true, filterValue: (r) => r.category, render: (r) => (
      <span className="inline-flex items-center gap-2 font-medium">
        <span className="h-7 w-7 rounded-lg bg-rose-500/10 text-rose-600 grid place-items-center shrink-0"><Wallet className="h-3.5 w-3.5" /></span>
        {r.category}
      </span>
    ) },
    { key: "source", label: "Sumber", sortable: true, filter: true, filterValue: (r) => srcName(r), sortValue: (r) => srcName(r), render: (r) => <span className="text-xs text-muted-foreground">{srcName(r)}</span> },
    { key: "notes", label: "Catatan", render: (r) => <span className="text-xs text-muted-foreground">{r.notes || "-"}</span> },
    { key: "amount", label: "Nominal", sortable: true, align: "right", sortValue: (r) => Number(r.amount || 0), render: (r) => <span className="money font-semibold text-rose-600 dark:text-rose-400">-{rupiah(r.amount)}</span> },
    { key: "_act", label: "", render: (r) => (
      <div className="flex justify-end"><Button variant="ghost" size="icon" data-testid={`btn-del-expense-${r.id}`} onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5" /></Button></div>
    ) },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Kas Keluar</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Pengeluaran</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-manage-installments" variant="outline" className="gap-2" onClick={() => setManageInst(true)}><Layers className="h-4 w-4" /> Kelola Cicilan</Button>
          <Button data-testid="btn-manage-expense-cat" variant="outline" className="gap-2" onClick={() => setManage(true)}><Settings2 className="h-4 w-4" /> Kelola Kategori</Button>
          <Button data-testid="btn-new-expense" onClick={() => setOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> Pengeluaran</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-4 bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-800">
          <p className="text-xs uppercase tracking-widest text-rose-700 dark:text-rose-400 font-semibold">Total Pengeluaran ({allMode ? "semua data" : monthInputLabel(monthSel)})</p>
          <p className="mt-1 money text-2xl font-bold text-rose-700 dark:text-rose-400 tracking-tight">{rupiah(total)}</p>
        </Card>
        <Card className="p-4 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-800">
          <p className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-400 font-semibold flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" /> Saldo Cicilan (belum jadi beban)</p>
          <p className="mt-1 money text-2xl font-bold text-amber-700 dark:text-amber-400 tracking-tight">{rupiah(totalSaldoCicilan)}</p>
          <p className="text-[11px] text-amber-700/70 dark:text-amber-400/70 mt-0.5">{activeInst.length} cicilan berjalan · dihitung ke total saldo agar untung tak minus</p>
        </Card>
      </div>

      {activeInst.length > 0 && (
        <Card className="p-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Cicilan Berjalan</p>
          <div className="space-y-2.5">
            {activeInst.map((i) => <InstallmentCard key={i.id} inst={i} onChanged={loadInst} onDeleted={load} />)}
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Input
          type="month"
          data-testid="expense-month"
          value={monthSel}
          onChange={(e) => { setMonthSel(e.target.value); setAllMode(false); }}
          className="h-9 w-[170px]"
          disabled={allMode}
        />
        <Button variant={allMode ? "default" : "outline"} size="sm" className="h-9" data-testid="expense-all-months" onClick={() => setAllMode((v) => !v)}>
          {allMode ? "Menampilkan semua" : "Semua bulan"}
        </Button>
      </div>

      <Card className="p-4">
        <DataTable
          columns={columns}
          rows={visibleList}
          testid="expense-table"
          zoomable
          groupBy={{ key: (r) => monthKeyWIB(r.date), label: (r) => monthLabelWIB(r.date), subKey: (r) => dayKeyWIB(r.date), subLabel: (r) => dayLabelWIB(r.date) }}
          emptyText='Belum ada pengeluaran. Klik "Pengeluaran".'
        />
      </Card>

      <ExpenseForm open={open} onClose={() => setOpen(false)} apps={apps} cats={cats} onSaved={load} />
      <CategoryManager open={manage} onClose={() => setManage(false)} cats={cats} onChanged={loadCats} />
      <InstallmentsManager open={manageInst} onClose={() => setManageInst(false)} installments={installments} onChanged={load} />
    </div>
  );
}

function ExpenseForm({ open, onClose, apps, cats, onSaved }) {
  const [form, setForm] = useState({ category: "", amount: "", source: "kas", source_id: "", notes: "" });
  const [cicil, setCicil] = useState(false);
  const [days, setDays] = useState(4);
  const [dt, setDt] = useState(wibNowLocal());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setForm({ category: cats[0]?.name || "", amount: "", source: "kas", source_id: "", notes: "" }); setCicil(false); setDays(4); setDt(wibNowLocal()); }
    // eslint-disable-next-line
  }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const schedule = cicil && form.amount ? computeSchedule(toNum(form.amount), days, dt) : [];
  const perDay = schedule.length ? schedule[0].amount : 0;
  const payoff = schedule.length ? schedule[schedule.length - 1].date : null;
  // Berapa slot yang tanggalnya <= hari ini -> akan OTOMATIS tercatat saat disimpan
  const _todayEnd = (() => { const x = new Date(); x.setHours(23, 59, 59, 999); return x; })();
  const autoPosted = schedule.filter((s) => s.date <= _todayEnd).length;

  const save = async () => {
    if (!form.category) return toast.error("Pilih kategori");
    if (!form.amount) return toast.error("Isi nominal");
    if (form.source === "app" && !form.source_id) return toast.error("Pilih aplikasi sumber");
    setSaving(true);
    try {
      if (cicil) {
        const n = parseInt(days) || 0;
        if (n < 2) { toast.error("Cicilan minimal 2 hari"); setSaving(false); return; }
        await api.post("/expense-installments", {
          category: form.category, total_amount: toNum(form.amount), days: n,
          source: form.source, source_id: form.source_id || null, notes: form.notes || null,
          start_date: wibLocalToUTCISO(dt),
        });
        toast.success("Cicilan pengeluaran dibuat");
      } else {
        await api.post("/expenses", { ...form, amount: toNum(form.amount), date: wibLocalToUTCISO(dt) });
        toast.success("Pengeluaran tercatat");
      }
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Pengeluaran Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>
                {cats.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">Belum ada kategori</div>}
                {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>{cicil ? "Total Pengeluaran" : "Nominal"}</Label><MoneyInput data-testid="expense-amount" value={form.amount} onChange={(v) => set("amount", v)} /></div>
          <div>
            <Label>Sumber Dana</Label>
            <Select value={form.source} onValueChange={(v) => { set("source", v); set("source_id", ""); }}>
              <SelectTrigger data-testid="expense-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="kas">Kas Fisik</SelectItem>
                <SelectItem value="app">Aplikasi / Bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.source === "app" && (
            <div>
              <Label>Pilih Aplikasi/Bank</Label>
              <Select value={form.source_id} onValueChange={(v) => set("source_id", v)}>
                <SelectTrigger data-testid="expense-source-app"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                <SelectContent>{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {rupiah(a.current_balance)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Catatan</Label><Textarea data-testid="expense-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>

          <div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{cicil ? "Tanggal mulai (WIB)" : "Tanggal (WIB)"}</Label>
                <Input data-testid="expense-date" type="date" value={(dt.split("T")[0]) || ""} onChange={(e) => setDt(`${e.target.value}T${dt.split("T")[1] || "00:00"}`)} />
              </div>
              <div>
                <Label>Jam (WIB)</Label>
                <Input data-testid="expense-time" type="time" value={(dt.split("T")[1] || "").slice(0, 5)} onChange={(e) => setDt(`${dt.split("T")[0]}T${e.target.value}`)} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Otomatis terisi tanggal & jam sekarang (WIB), bisa diubah manual.</p>
          </div>

          {/* ==== Mode Cicil / Sebar beban ==== */}
          <Card className="p-3 bg-amber-50/60 dark:bg-amber-500/5 border-amber-200 dark:border-amber-900 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5"><Layers className="h-4 w-4 text-amber-600" /> Cicil / Sebar ke beberapa hari</p>
                <p className="text-[11px] text-muted-foreground">Uang keluar penuh hari ini, tapi beban dibagi rata per hari agar untung tak minus.</p>
              </div>
              <Switch data-testid="expense-cicil" checked={cicil} onCheckedChange={setCicil} />
            </div>
            {cicil && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="shrink-0">Jumlah hari</Label>
                  <Input data-testid="expense-cicil-days" type="number" min={2} max={60} value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-24" />
                  <span className="text-xs text-muted-foreground">(Sabtu+Minggu = 1 hari)</span>
                </div>
                {form.amount && perDay > 0 && (
                  <div className="rounded-md bg-white/70 dark:bg-white/5 p-2.5 text-sm space-y-1">
                    <p className="flex justify-between"><span className="text-muted-foreground">Cicilan / hari</span><span className="money font-semibold text-amber-700 dark:text-amber-400">{rupiah(perDay)}</span></p>
                    <p className="flex justify-between"><span className="text-muted-foreground">Hari pertama dicatat</span><span>{dateShort(schedule[0].date)}</span></p>
                    <p className="flex justify-between"><span className="text-muted-foreground">Prediksi lunas</span><span className="font-semibold">{dateShort(payoff)}</span></p>
                    {autoPosted > 1 && (
                      <p className="flex justify-between text-emerald-700 dark:text-emerald-400 pt-1 border-t border-amber-200/60 dark:border-amber-900/60 mt-1">
                        <span>Perkiraan langsung tercatat</span><span className="font-semibold">s/d {autoPosted} dari {schedule.length} hari</span>
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground pt-1 border-t border-amber-200/60 dark:border-amber-900/60 mt-1">Cicilan otomatis hanya dibebankan pada hari yang untungnya tetap di atas Rp 20.000; bila tidak, digeser ke hari berikutnya (batas akhir bulan).</p>
                  </div>
                )}
              </>
            )}
          </Card>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-expense" onClick={save} disabled={saving} className="flex-1">{cicil ? "Buat Cicilan" : "Simpan"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InstallmentCard({ inst, onChanged, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const recompute = async () => {
    setBusy(true);
    try {
      await api.post(`/expense-installments/rebalance`);
      toast.success("Penempatan cicilan dihitung ulang");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!(await confirmDialog({ title: "Hapus cicilan ini?", description: "Semua beban terkait akan dihapus (uang tidak dikembalikan otomatis)." }))) return;
    setBusy(true);
    try { await api.delete(`/expense-installments/${inst.id}`); toast.success("Cicilan dihapus"); onDeleted?.(); }
    finally { setBusy(false); }
  };
  const pct = Math.round((inst.posted_count / inst.days) * 100);
  return (
    <div data-testid={`installment-${inst.id}`} className="rounded-lg border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold flex items-center gap-1.5">{inst.category}
            <Badge variant="secondary" className="font-normal">{inst.posted_count}/{inst.days} hari</Badge>
          </p>
          <p className="text-xs text-muted-foreground truncate">{inst.notes || "-"} · Total {rupiah(inst.total_amount)} · {rupiah(inst.next_amount || inst.total_amount / inst.days)}/hari</p>
        </div>
        <Button size="icon" variant="ghost" data-testid={`btn-del-inst-${inst.id}`} onClick={del} disabled={busy}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${pct}%` }} /></div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-amber-700 dark:text-amber-400 font-medium">Sisa saldo cicilan: {rupiah(inst.outstanding)}</span>
        <span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Cicilan berikut: {inst.next_due ? dateShort(inst.next_due) : "-"} · Lunas: {inst.payoff_date ? dateShort(inst.payoff_date) : "-"}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-emerald-700 dark:text-emerald-400 leading-tight">Otomatis: cicilan hanya dibebankan di hari yang untungnya tetap di atas Rp 20.000; kalau tidak, digeser ke hari berikutnya (batas akhir bulan).</p>
        <Button size="sm" variant="outline" className="gap-1.5 shrink-0" data-testid={`btn-recompute-inst-${inst.id}`} onClick={recompute} disabled={busy}><CheckCircle2 className="h-3.5 w-3.5" /> Hitung ulang</Button>
      </div>
    </div>
  );
}

function InstallmentsManager({ open, onClose, installments, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const del = async (inst) => {
    if (!(await confirmDialog({ title: `Hapus cicilan "${inst.category}"?`, description: "Seluruh beban terkait cicilan ini akan dihapus. Uang yang sudah keluar tidak dikembalikan otomatis." }))) return;
    setBusyId(inst.id);
    try {
      await api.delete(`/expense-installments/${inst.id}`);
      toast.success("Cicilan dihapus");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus cicilan");
    } finally { setBusyId(null); }
  };
  const sorted = [...(installments || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="installments-manager">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-amber-600" /> Kelola Cicilan</DialogTitle></DialogHeader>
        <div className="space-y-2.5">
          {sorted.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Belum ada cicilan.</p>}
          {sorted.map((inst) => {
            const done = (inst.status !== "active") || Number(inst.outstanding || 0) <= 0;
            return (
              <div key={inst.id} data-testid={`manage-inst-${inst.id}`} className="rounded-lg border p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold flex items-center gap-1.5 flex-wrap">{inst.category}
                    <Badge variant="secondary" className="font-normal">{inst.posted_count}/{inst.days} hari</Badge>
                    <Badge className={done ? "bg-emerald-500 hover:bg-emerald-500" : "bg-amber-500 hover:bg-amber-500"}>{done ? "Selesai" : "Berjalan"}</Badge>
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{inst.notes || "-"} · Total {rupiah(inst.total_amount)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Sisa saldo cicilan: {rupiah(inst.outstanding || 0)}</p>
                </div>
                <Button size="icon" variant="ghost" className="shrink-0 text-muted-foreground hover:text-destructive" data-testid={`btn-del-inst-manage-${inst.id}`} onClick={() => del(inst)} disabled={busyId === inst.id}><Trash2 className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryManager({ open, onClose, cats, onChanged }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return toast.error("Isi nama kategori");
    try {
      await api.post("/expenses/categories", { name: name.trim() });
      setName(""); toast.success("Kategori ditambah"); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah kategori");
    }
  };
  const del = async (c) => {
    if (!(await confirmDialog({ title: `Hapus kategori "${c.name}"?` }))) return;
    await api.delete(`/expenses/categories/${c.id}`); toast.success("Kategori dihapus"); onChanged?.();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Kelola Kategori Pengeluaran</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input data-testid="expense-cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kategori baru..." onKeyDown={(e) => e.key === "Enter" && add()} />
            <Button data-testid="btn-add-expense-cat" onClick={add} className="gap-1"><Plus className="h-4 w-4" /> Tambah</Button>
          </div>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
            {cats.map((c) => (
              <div key={c.id} data-testid={`expense-cat-${c.id}`} className="flex items-center justify-between border-b border-border/60 pb-1.5 text-sm">
                {c.name}
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`btn-del-expense-cat-${c.id}`} onClick={() => del(c)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
